/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  BuilderPermission,
  DashboardAverageItem,
  DashboardChartType,
  DashboardWidgetsCode,
  DashboardWidgetsType,
  getLocaleIndex,
  OfficeType,
  SalesLeadsBudgetsLegend,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { EnumDictionary, getRouteInitData, HelpID, NumberTypes } from '@gh/common-shared';
import { Enum } from '@gh/core-data';
import { TranslationParams, TranslationService } from '@gh/core-mls';
import { DialogService, NotificationService, WaitingService } from '@gh/core-ui';
import {
  GridDefinitionFactory,
  GridOptionsExt,
  LayoutBasedGridDefinition,
  ManualGridDefinition,
  useTooltipInColumn,
} from '@gh/core-ui-grid';
import { dispatchResizeWindow } from '@gh/core-util';
import { map$, filter$, switchMap$ } from '@gh/rx/operators';
import { ColDef } from 'ag-grid-community';
import {
  assign,
  cloneDeep,
  difference,
  find,
  findIndex,
  forEach,
  head,
  includes,
  isEmpty,
  isObject,
  isString,
  join,
  keys,
  lowerFirst,
  mapValues,
  pickBy,
  pullAt,
  startsWith,
} from 'lodash';
import {
  AverageBuildTimeItem,
  ConversionRatesItem,
  DashboardBusinessService,
  DashboardManageDialogConfig,
  MaintenanceTasksOverdue,
  Office,
  PreconstructionBuildTimesListItem,
  PriorityAndStageItem,
  SalespersonTotalItem,
  TopCustomersByEarliestContact,
  TopCustomersByPriority,
  WidgetDataLoader,
  WidgetRenderer,
  WidgetsModel,
  WidgetState,
} from '../../resources';
import { dashboardManageDialogRoute } from '../dashboard-manage-dialog';

@Component({
  standalone: false,
  selector: 'gh-builder-dashboard',
  templateUrl: 'dashboard.component.html',
  styleUrls: ['dashboard.component.scss'],
  providers: [DashboardBusinessService],
})
export class DashboardComponent implements OnInit, OnDestroy {
  dashboardChartTypeDict: EnumDictionary<DashboardChartType>;
  currentLocaleIndex: number;
  type: DashboardWidgetsType;
  offices: Office[];
  widgets: WidgetsModel[] = [];
  helpId: HelpID;
  averageBuildTime: AverageBuildTimeItem[];
  averageBuildTimeGridDefinition: ManualGridDefinition;
  averageBuildTimeProps: WidgetsModel;
  averageContractPrice: DashboardAverageItem[];
  averageContractPriceProps: WidgetsModel;
  averageHouseCostChange: DashboardAverageItem[];
  averageHouseCostChangeProps: WidgetsModel;
  averagePreconstructionBuildTimes: google.visualization.ChartSpecs;
  averagePreconstructionBuildTimesProps: WidgetsModel;
  averageTimeFromSfToDeposit: DashboardAverageItem[];
  averageTimeFromSfToDepositProps: WidgetsModel;
  conversionRates: ConversionRatesItem[];
  conversionRatesProps: WidgetsModel;
  conversionRatesGridDefinition: LayoutBasedGridDefinition;
  grossAndNetProfit: google.visualization.ChartSpecs;
  grossAndNetProfitProps: WidgetsModel;
  grossProfitAndOverheads: google.visualization.ChartSpecs;
  grossProfitAndOverheadsProps: WidgetsModel;
  maintenanceTasksOverdue: MaintenanceTasksOverdue[];
  maintenanceTasksOverdueProps: WidgetsModel;
  maintenanceTasksOverdueGridDefinition: LayoutBasedGridDefinition;
  monthlySalesSlabs: google.visualization.ChartSpecs;
  monthlySalesSlabsProps: WidgetsModel;
  preconstructionBuildTimes: PreconstructionBuildTimesListItem[];
  preconstructionBuildTimesProps: WidgetsModel;
  preconstructionBuildTimesGridDefinition: LayoutBasedGridDefinition;
  priorityAndStage: PriorityAndStageItem[];
  priorityAndStageProps: WidgetsModel;
  priorityAndStageGridDefinition: ManualGridDefinition;
  salespersonTotal: SalespersonTotalItem[];
  salespersonTotalProps: WidgetsModel;
  salespersonTotalGridDefinition: ManualGridDefinition;
  salesLeadsBudgets: google.visualization.ChartSpecs;
  salesLeadsBudgetsProps: WidgetsModel;
  topCustomersByEarliestContact: TopCustomersByEarliestContact[];
  topCustomersByEarliestContactProps: WidgetsModel;
  topCustomersByEarliestContactGridDefinition: LayoutBasedGridDefinition;
  topCustomersByPriority: TopCustomersByPriority[];
  topCustomersByPriorityProps: WidgetsModel;
  topCustomersByPriorityGridDefinition: LayoutBasedGridDefinition;

  private widgetStates: Hash<WidgetState> = {};
  private loaders: Hash<WidgetDataLoader> = {};
  private renderers: Hash<WidgetRenderer> = {};

  constructor(
    private route: ActivatedRoute,
    private translations: TranslationService,
    private notifications: NotificationService,
    private gridDefinitionFactory: GridDefinitionFactory,
    private waitingService: WaitingService,
    private userContext: BuilderUserContext,
    private dashboard: DashboardBusinessService,
    private dialogService: DialogService,
  ) {
  }

  ngOnInit(): void {
    const { dashboardChartTypeDict, widgets } = getRouteInitData(this.route);

    this.dashboardChartTypeDict = dashboardChartTypeDict;

    if (this.isFranchisee) {
      this.type = DashboardWidgetsType.FRANCHISEE;
      this.loaders = this.dashboard.createFranchiseeLoaders();
      this.renderers = this.initFranchiseRenderers();
      this.helpId = 'dashboard/franchisee';
    } else if (this.isSales) {
      this.type = DashboardWidgetsType.SALESPERSON;
      this.loaders = this.dashboard.createSalesLoaders();
      this.renderers = this.initSalesRenderers();
      this.helpId = 'dashboard/sales';
    } else if (this.isMaster) {
      this.type = DashboardWidgetsType.MASTER;
      this.loaders = this.dashboard.createMasterLoaders();
      this.renderers = this.createMasterRenderers();
      this.helpId = 'dashboard/master';
    } else if (this.isCorporate) {
      this.helpId = 'dashboard/corporate';
      this.type = DashboardWidgetsType.CORPORATE;
      this.loaders = this.dashboard.createCorporateLoaders();
      this.renderers = this.initCorporateRenderers();
    }
    this.refreshWidgetList(widgets);

    this.currentLocaleIndex = getLocaleIndex(this.userContext.locale);
  }

  ngOnDestroy(): void {
    // interrupt all current loadings
    forEach(this.widgetStates, (state) => {
      if (state.subscription) {
        state.subscription.unsubscribe();
        state.subscription = void 0;
      }
    });
  }

  get isCorporate(): boolean {
    return this.userContext.corporateView && !this.isTemplateOrMasterOffice;
  }

  get isMaster(): boolean {
    return this.userContext.masterView && !this.isTemplateOrMasterOffice;
  }

  get isFranchisee(): boolean {
    return this.userContext.officeView
      && this.userContext.hasPermission(BuilderPermission.Dashboard.Franchise.VIEW)
      && !this.isTemplateOrMasterOffice
      && (this.userContext.hasSystemRole(SystemRoleBit.Franchisee) || this.userContext.isSwitched());
  }

  get isSales(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.SalesPerson)
      && !this.userContext.hasSystemRole(SystemRoleBit.Franchisee)
      && this.userContext.hasPermission(BuilderPermission.Dashboard.Sales.VIEW)
      && !this.isTemplateOrMasterOffice;
  }

  get hasManagePermission(): boolean {
    if (this.isFranchisee) {
      return this.userContext.hasPermission(BuilderPermission.Dashboard.Franchise.MANAGE);
    } else if (this.isSales) {
      return this.userContext.hasPermission(BuilderPermission.Dashboard.Sales.MANAGE);
    }

    return this.isCorporate || this.isMaster;
  }

  get isTemplateOrMasterOffice(): boolean {
    const office = this.userContext.office;

    if (!office) {
      return false;
    }

    return office.type === OfficeType.Template || office.type === OfficeType.Master;
  }

  get slbSales(): number {
    return this.getSlbLegend(Enum.toValue(DashboardChartType, DashboardChartType.Sales));
  }

  get slbLeads(): number {
    return this.getSlbLegend(Enum.toValue(DashboardChartType, DashboardChartType.Leads));
  }

  get slbSalesBudget(): number {
    return this.getSlbLegend(Enum.toValue(DashboardChartType, DashboardChartType.SalesBudgets));
  }

  get slbLeadsBudget(): number {
    return this.getSlbLegend(Enum.toValue(DashboardChartType, DashboardChartType.LeadsBudgets));
  }

  get dashboardClass(): string {
    const widgets = this.widgets.filter((widget) => widget.isSelected);
    return `dashboard-${widgets.length}`;
  }

  initCorporateRenderers(): Hash<WidgetRenderer> {
    return {
      [DashboardWidgetsCode.averageBuildTime]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.averageBuildTime,
        () => this.initManualGrid(DashboardWidgetsCode.averageBuildTime, [], {
          rowClass: ['row-nowrap', 'no-pad-center'],
          getRowId: (params) => String(params.data._rowId != null ? String(params.data._rowId) : head(keys(params.data))!),
        })),
      [DashboardWidgetsCode.salespersonTotal]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.salespersonTotal,
        () => this.initManualGrid(DashboardWidgetsCode.salespersonTotal, [], {
          rowClass: ['row-right'],
          rowHeight: 50,
          getRowId: (params) => String(params.data._rowId != null ? String(params.data._rowId) : head(keys(params.data))!),
        })),
      [DashboardWidgetsCode.averagePreconstructionBuildTimes]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.averagePreconstructionBuildTimes,
        () => this.initChartData(DashboardWidgetsCode.averagePreconstructionBuildTimes)),
      [DashboardWidgetsCode.grossAndNetProfit]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.grossAndNetProfit,
        () => this.initChartData(DashboardWidgetsCode.grossAndNetProfit)),
      [DashboardWidgetsCode.monthlySalesSlabs]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.monthlySalesSlabs,
        () => this.initChartData(DashboardWidgetsCode.monthlySalesSlabs)),
      [DashboardWidgetsCode.salesLeadsBudgets]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.salesLeadsBudgets,
        () => this.initChartData(DashboardWidgetsCode.salesLeadsBudgets)),
    };
  }

  createMasterRenderers(): Hash<WidgetRenderer> {
    return {
      [DashboardWidgetsCode.averageBuildTime]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.averageBuildTime,
        () => this.initManualGrid('averageBuildTime', [], {
          rowClass: ['row-nowrap', 'no-pad-center'],
          getRowId: (params) => String(params.data._rowId != null ? String(params.data._rowId) : head(keys(params.data))!),
        })),
      [DashboardWidgetsCode.averagePreconstructionBuildTimes]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.averagePreconstructionBuildTimes,
        () => this.initChartData('averagePreconstructionBuildTimes')),
      [DashboardWidgetsCode.grossAndNetProfit]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.grossAndNetProfit,
        () => this.initChartData('grossAndNetProfit')),
      [DashboardWidgetsCode.grossProfitAndOverheads]: this.createDefaultWidgetRenderer(
        {
          [DashboardWidgetsCode.grossProfitAndOverheads]: 'data',
          offices: 'offices',
        },
        () => this.initChartData('grossProfitAndOverheads')),
      [DashboardWidgetsCode.monthlySalesSlabs]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.monthlySalesSlabs,
        () => this.initChartData('monthlySalesSlabs')),
      [DashboardWidgetsCode.salesLeadsBudgets]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.salesLeadsBudgets,
        () => this.initChartData('salesLeadsBudgets')),
    };
  }

  initFranchiseRenderers(): Hash<WidgetRenderer> {
    return {
      [DashboardWidgetsCode.maintenanceTasksOverdue]: this.createDefaultWidgetRenderer(
        {
          [DashboardWidgetsCode.maintenanceTasksOverdue]: 'data',
          maintenanceTasksOverdueLayout: 'gridLayout',
        },
        () => this.initLayoutGridDefinition(
          'maintenanceTasksOverdue', {
            clientName: {
              minWidth: 300,
            },
          },
          {
            rowClass: ['row-nowrap'],
          })),
      [DashboardWidgetsCode.preconstructionBuildTimes]: this.createDefaultWidgetRenderer(
        {
          [DashboardWidgetsCode.preconstructionBuildTimes]: 'data',
          preconstructionBuildTimesLayout: 'gridLayout',
        },
        () => this.initLayoutGridDefinition(
          'preconstructionBuildTimes',
          {
            preconstructionJobTime: {
              cellClass: 'cell-right',
            },
            buildJobTime: {
              headerClass: 'cell-right-pad',
              cellClass: 'cell-right',
            },
          },
          {
            rowClass: ['row-nowrap'],
          })),
      [DashboardWidgetsCode.averageContractPrice]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.averageContractPrice,
        () => this.initWidgetData('averageContractPrice')),
      [DashboardWidgetsCode.averageHouseCostChange]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.averageHouseCostChange,
        () => this.initWidgetData('averageHouseCostChange')),
      [DashboardWidgetsCode.grossProfitAndOverheads]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.grossProfitAndOverheads,
        () => this.initChartData('grossProfitAndOverheads')),
      [DashboardWidgetsCode.salesLeadsBudgets]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.salesLeadsBudgets,
        () => this.initChartData('salesLeadsBudgets')),
    };
  }

  initSalesRenderers(): Hash<WidgetRenderer> {
    return {
      [DashboardWidgetsCode.conversionRates]: this.createDefaultWidgetRenderer(
        {
          [DashboardWidgetsCode.conversionRates]: 'data',
          conversionRatesLayout: 'gridLayout',
        },
        () => this.initLayoutGridDefinition(
          'conversionRates',
          {
            lblCol1: {
              minWidth: 120,
              cellRenderer: (params) => {
                const count = NumberTypes.Count.format(params.data.count);
                return `<div>${params.data.lblCol1}</div><div>${count}</div>`;
              },
            },
            lblCol2: {
              minWidth: 120,
            },
            lastYear: {
              cellClass: 'cell-right',
            },
            total: {
              cellClass: 'cell-right',
            },
          })),
      [DashboardWidgetsCode.topCustomersByEarliestContact]: this.createDefaultWidgetRenderer(
        {
          [DashboardWidgetsCode.topCustomersByEarliestContact]: 'data',
          topCustomersByEarliestContactLayout: 'gridLayout',
        },
        () => this.initLayoutGridDefinition(
          'topCustomersByEarliestContact',
          {
            clientName: {
              minWidth: 225,
            },
            value: {
              cellClass: 'cell-right',
            },
          })),
      [DashboardWidgetsCode.topCustomersByPriority]: this.createDefaultWidgetRenderer(
        {
          [DashboardWidgetsCode.topCustomersByPriority]: 'data',
          topCustomersByPriorityLayout: 'gridLayout',
        },
        () => this.initLayoutGridDefinition(
          'topCustomersByPriority',
          {
            clientName: {
              minWidth: 290,
            },
            value: {
              cellClass: 'cell-right',
            },
          })),
      [DashboardWidgetsCode.priorityAndStage]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.priorityAndStage,
        () => this.initManualGrid(
          'priorityAndStage',
          [{
            field: 'lbl_priority',
            minWidth: 150,
          }],
          {
            rowClass: ['row-right'],
          })),
      [DashboardWidgetsCode.averageTimeFromSfToDeposit]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.averageTimeFromSfToDeposit,
        () => this.initWidgetData('averageTimeFromSfToDeposit')),
      [DashboardWidgetsCode.salesLeadsBudgets]: this.createDefaultWidgetRenderer(
        DashboardWidgetsCode.salesLeadsBudgets,
        () => this.initChartData('salesLeadsBudgets')),
    };
  }

  onOfficeSelect(widget: WidgetsModel, event: MatSelectChange): void {
    this.refreshWidget(widget, event.value);
  }

  getMaintenanceTaskTooltip(task: MaintenanceTasksOverdue): string {
    const title = this.translations.get('lbl_description');
    return `${title}: ${task.description}. ${task.comment}`;
  }

  getWidgetName(widget: WidgetsModel): string {
    return widget.name[this.currentLocaleIndex];
  }

  getWidgetState(widget: WidgetsModel): WidgetState {
    return this.widgetStates[widget.code];
  }

  setWidgetsProps(): void {
    this.widgets.forEach((widget: WidgetsModel) => {
      const widgetProps = `${widget.code}Props`;
      this[widgetProps] = widget;
    });
  }

  onManageDashboard(): void {
    const origWidgets = cloneDeep(this.widgets);

    this.dialogService.openWithInput(
      dashboardManageDialogRoute,
      <DashboardManageDialogConfig>{
        widgets: origWidgets,
        localeIndex: this.currentLocaleIndex,
      },
    ).afterClosed().pipe(
      filter$(Boolean),
      switchMap$((widgets: WidgetsModel[]) =>
        this.dashboard.updateWidgets(this.type, widgets).pipe(this.waitingService.operator(), map$(() => widgets))))
      .subscribe(
        (widgets) => {
          this.refreshWidgetList(widgets);
          dispatchResizeWindow();
        },
        this.notifications.processAll,
      );
  }

  private initLayoutGridDefinition(
    entity: string,
    colDefs: Hash<ColDef> = {},
    options: GridOptionsExt = {},
  ): DashboardComponent {
    if (!this[entity]) {
      return this;
    }

    this.translateEntity(entity);

    const definitionEntity = `${entity}GridDefinition`;
    const layoutEntity = `${entity}Layout`;

    this[definitionEntity] = this.gridDefinitionFactory.buildLayoutBased(colDefs, {
      ...this.getGridOptions(),
      ...options,
    });
    this[definitionEntity].bind(this[layoutEntity]);

    return this;
  }

  private initManualGrid(entity: string, colsDef: ColDef[] = [], options: GridOptionsExt = {}): DashboardComponent {
    if (isEmpty(this[entity])) {
      return this;
    }

    this.translateEntity(entity);
    keys(head(this[entity]))
      .filter((field: string) => !startsWith(field, '_'))
      .forEach((field: string) => {
      const colDefIndex = findIndex(colsDef, (def) => def.field === field);
      const colDef = pullAt(colsDef, colDefIndex);
      const colOptions = {
        ...(colDef.length ? colDef[0] : {}),
        field,
        headerName: startsWith(field, 'lbl') ? this.translations.get(field) : field,
        sortable: false,
        ...useTooltipInColumn({
          tooltip: (params: any) => join(this.dashboard.gridTooltips[`${field}${params.rowIndex}`], ', '),
        }),
      };

      colsDef.push(colOptions);
    });

    const definitionEntity = `${lowerFirst(entity)}GridDefinition`;
    this[definitionEntity] = this.gridDefinitionFactory.buildManual(colsDef, {
      getRowId: (params) => String(head(keys(params.data))!),
      ...this.getGridOptions(),
      ...options,
    });

    return this;
  }

  private initChartData(entity: string): DashboardComponent {
    if (!this[entity] || !this[entity].dataTable) {
      return this;
    }

    const specs = this[entity];

    head(specs.dataTable as any[][])!.map((col: google.visualization.DataObjectColumn) => {
      if (col.label) {
        col.label = startsWith(col.label, 'lbl')
          ? this.translations.get(col.label)
          : this.dashboardChartTypeDict.get(Enum.byName(DashboardChartType, col.label));
      }
    });

    (specs.dataTable as any[][]).map((rows: any[], dataIndex: number) => {
      rows.map((col: any, rowIndex: number) => {
        (specs.dataTable as any[][])[dataIndex][rowIndex] = isObject(col) && startsWith(col['label'], 'lbl')
          ? this.translations.get(col['label'], col['value'] as TranslationParams) : col;
      });
    });

    return this;
  }

  private initWidgetData(entity: string): DashboardComponent {
    if (!this[entity]) {
      return this;
    }

    this[entity].map((item: DashboardAverageItem) => {
      item.label = this.translations.get(item.label);
      item.subtitle = item.subtitle ? this.translations.get(item.subtitle) : '';
    });

    return this;
  }

  private translateEntity(entity: string): void {
    this[entity].map((item: any) => {
      keys(item).map((key: string) => {
        const cell = item[key];
        item[key] = isObject(cell)
          ? this.translations.get(
              (cell as { label: string }).label,
              (cell as { label: string; value: Record<string, string | number> }).value as unknown as TranslationParams,
            )
          : startsWith(item[key], 'lbl')
            ? this.translations.get(item[key])
            : item[key];
      });
    });
  }

  private getSlbLegend(type: DashboardChartType): number {
    if (!this.salesLeadsBudgets) {
      return 0;
    }

    const legendItem = (this.salesLeadsBudgets as any)
      .legend
      .find((item: SalesLeadsBudgetsLegend) => item.type === type);

    return legendItem ? legendItem.value : 0;
  }

  private getGridOptions(): GridOptionsExt {
    const manualCols = ['lbl_dashboard_param'];

    return {
      headerHeight: 40,
      rowHeight: 30,
      suppressColumnVirtualisation: true,
      onRowDataUpdated: (event) => {
        const colsDef: string[] = [];
        event.api
          .getAllGridColumns()
          .filter((col) => -1 === manualCols.indexOf(col.getColId()))
          .forEach((col) => colsDef.push(col.getColId()));

        setTimeout(() => event.api.autoSizeColumns(colsDef), 100);
      },
    };
  }

  private refreshWidget(widget: WidgetsModel, query: any): void {
    const { code } = widget;
    const state = this.widgetStates[code];

    state.loaded = false;
    state.loading = true;
    state.error = void 0;

    if (state.subscription) {
      state.subscription.unsubscribe();
      state.subscription = void 0;
    }
    state.subscription = this.loaders[code].update(widget, query).subscribe(
      (data) => {
        this.renderers[code].update(widget, data);
        state.subscription = void 0;
        state.loading = false;
        state.loaded = true;
      },
      (error) => {
        state.subscription = void 0;
        state.loading = false;
        state.error = error;
        state.loaded = true;
      });
  }

  private refreshWidgetList(nextWidgets: WidgetsModel[]): void {
    const currentWidgetCodes = keys(pickBy(this.widgetStates, ({ loaded, loading }) => loaded || loading));
    const nextWidgetCodes = nextWidgets.filter(({ isSelected }) => isSelected).map<string>(({ code }) => code);
    const deletedWidgetCodes = difference(currentWidgetCodes, nextWidgetCodes);
    const newWidgetCodes = difference(nextWidgetCodes, currentWidgetCodes);

    const currentWidgets = this.widgets;
    this.widgets = nextWidgets;

    deletedWidgetCodes.forEach((code) => {
      const widget = find(currentWidgets, <WidgetsModel>{ code })!;
      this.renderers[code].destroy(widget);

      const state = this.widgetStates[code];
      state.loaded = false;
      state.loading = false;
      state.error = void 0;
      if (state.subscription) {
        state.subscription.unsubscribe();
        state.subscription = void 0;
      }

      this[code] = void 0;
    });

    const newWidgets = nextWidgets
      .filter((widget) => includes(newWidgetCodes, widget.code))
      .filter((widget) => this.loaders[widget.code]);
    newWidgets.forEach((widget) => {
      const { code } = widget;
      const loader = this.loaders[code];
      const state: WidgetState = {
        loaded: false,
        loading: true,
        error: void 0,
        subscription: loader.init(widget).subscribe(
          (data) => {
            state.subscription = void 0;
            state.loading = false;
            state.loaded = true;
            this.renderers[code].init(widget, data);
          },
          (error) => {
            state.subscription = void 0;
            state.loading = false;
            state.error = error;
            state.loaded = true;
          }),
      };
      this.widgetStates[code] = state;
    });

    this.setWidgetsProps();
  }

  private createDefaultWidgetRenderer(rewrite: DashboardWidgetsCode | Hash<string>,
                                      render: ProcedureFn<any>): WidgetRenderer {
    return {
      init: (_, data) => {
        // assign loaded data to the component using rewrite rules
        if (isString(rewrite)) {
          this[rewrite.toString()] = data;
        } else {
          assign(this, mapValues(rewrite, (dataKey) => data[dataKey]));
        }
        render(data);
      },
      update: (_, data) => {
        // assign loaded data to the component using rewrite rules
        if (isString(rewrite)) {
          this[rewrite.toString()] = data;
        } else {
          // rewrite only properties which exist on the loaded object
          assign(this, mapValues(
            pickBy(rewrite, (dataKey) => Object.prototype.hasOwnProperty.call(data, dataKey)),
            (dataKey) => data[dataKey]));
        }
        render(data);
      },
      destroy: () => {
        if (isString(rewrite)) {
          this[rewrite.toString()] = void 0;
        } else {
          assign(this, mapValues(rewrite, () => void 0));
        }
      },
    };
  }
}
